class MarqueeComponent extends HTMLElement {
  constructor() {
    super();
    this.speed = 0.2;
    this.direction = 'ltr';
    this.paused = false;
    this.pauseOnHover = true;
    this.animationId = null;
    this.resizeRaf = null;
    this.translateX = 0;
    this.containerWidth = 0;
    this.itemWidth = 0;
    this.items = [];
    this.clones = [];
    this.isInitialized = false;
    this.marqueeVisible = true;
  }

  connectedCallback() {
    setTimeout(() => {
      this.init();
      this.setupEventListeners();
      this.startAnimation();
    }, 100);
  }

  disconnectedCallback() {
    this.stopAnimation();
    this.removeEventListeners();
  }

  init() {
    const speedSetting = this.dataset.speedSetting || 'normal';
    const speedMap = { slow: 0.05, normal: 0.2, fast: 0.4 };
    this.speed = speedMap[speedSetting] || 0.2;

    this.direction = this.dataset.direction || 'ltr';
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';

    this.container = this.querySelector('.marquee-container');
    this.content = this.querySelector('.marquee-content');

    if (!this.container || !this.content) return;

    this.items = Array.from(this.content.children);
    this.originalContentWidth = this.content.scrollWidth;

    this.updateDimensions();
    this.createClones();

    this.translateX = this.direction === 'rtl' ? -this.itemWidth : 0;
    this.updatePosition();
    this.isInitialized = true;
  }

  updateDimensions() {
    if (!this.container || !this.content) return;

    this.containerWidth = this.container.offsetWidth;
    this.itemWidth = this.content.scrollWidth;
  }

  createClones() {
    if (!this.content || this.items.length === 0) return;

    this.clones.forEach((clone) => {
      if (clone.parentNode) clone.parentNode.removeChild(clone);
    });
    this.clones = [];

    const minWidth = this.containerWidth * 3;
    const itemsTotalWidth = this.originalContentWidth;
    const setsNeeded = Math.ceil(minWidth / itemsTotalWidth) + 1;

    for (let i = 0; i < setsNeeded; i++) {
      this.items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.classList.add('marquee-clone');
        clone.setAttribute('aria-hidden', 'true');
        this.content.appendChild(clone);
        this.clones.push(clone);
      });
    }

    this.itemWidth = this.originalContentWidth;
  }

  updatePosition() {
    if (!this.content) return;
    this.content.style.transform = `translate3d(${this.translateX}px, 0, 0)`;
  }

  animate() {
    this.animationId = null;

    if (!this.isInitialized || this.paused || !this.marqueeVisible || document.hidden) {
      return;
    }

    if (this.direction === 'ltr') {
      this.translateX -= this.speed;
      if (this.translateX <= -this.itemWidth) {
        this.translateX = 0;
      }
    } else {
      this.translateX += this.speed;
      if (this.translateX >= 0) {
        this.translateX = -this.itemWidth;
      }
    }

    this.updatePosition();
    this.animationId = requestAnimationFrame(() => this.animate());
  }

  startAnimation() {
    if (this.animationId) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      if (this.content) this.content.style.transform = 'translate3d(0, 0, 0)';
      if (this.container) this.container.style.justifyContent = 'center';
      return;
    }

    if (!this.isInitialized || this.paused || !this.marqueeVisible || document.hidden) {
      return;
    }

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  stopAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  pause() {
    this.paused = true;
    this.stopAnimation();
  }

  resume() {
    this.paused = false;
    this.startAnimation();
  }

  setupEventListeners() {
    if (this.pauseOnHover) {
      this.mouseEnterHandler = () => this.pause();
      this.mouseLeaveHandler = () => this.resume();

      this.addEventListener('mouseenter', this.mouseEnterHandler);
      this.addEventListener('mouseleave', this.mouseLeaveHandler);
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = null;
        if (!this.container || !this.content) return;
        this.updateDimensions();
        this.createClones();
      });
    });
    this.resizeObserver.observe(this.container);

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          this.marqueeVisible = true;
          this.startAnimation();
        } else {
          this.marqueeVisible = false;
          this.stopAnimation();
        }
      },
      { rootMargin: '80px' }
    );
    this.intersectionObserver.observe(this);

    this.handleVisibilityChange = () => {
      if (document.hidden) {
        this.stopAnimation();
      } else if (this.marqueeVisible && !this.paused) {
        this.startAnimation();
      }
    };
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  removeEventListeners() {
    if (this.pauseOnHover) {
      this.removeEventListener('mouseenter', this.mouseEnterHandler);
      this.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}

const SPEED_MAP = {
  slow: 0.05,
  normal: 0.2,
  fast: 0.4,
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('marquee-component').forEach((marquee) => {
    const speedSetting = marquee.dataset.speedSetting;
    if (speedSetting && SPEED_MAP[speedSetting]) {
      marquee.dataset.speed = SPEED_MAP[speedSetting];
    }
  });
});
